/**
 * Populate params for each Strapi content type, kept in a separate file so
 * that it can be imported by scripts running outside of Next.js (e.g. via
 * ts-node) without pulling in React component dependencies.
 */

export type ContentTypePopulateConfig = {
	blocks: Record<string, [string, string][]>;
	topLevelPopulate: [string, string][];
};

export const CONTENT_TYPE_POPULATE_CONFIGS: Record<
	string,
	ContentTypePopulateConfig
> = {
	feature: {
		blocks: {
			'landing.text3-columns': [['[populate]', '*']],
			'landing.bullets': [['[populate]', '*']],
			'landing.faq': [['[populate]', '*']],
			'landing.horizontal-rail': [['[populate][imageWithDesc][populate]', '*']],
			'landing.hero': [
				['[populate][cta]', '*'],
				['[populate][image][populate]', '*'],
			],
			'landing.basic-block': [
				['[populate][cta]', '*'],
				['[populate][image][populate]', '*'],
			],
			'landing.vanilla-html': [['[populate][cta]', '*']],
			'landing.carousel': [['[populate][video][populate]', '*']],
			'landing.chat-and-post-card': [
				['[populate][chat][populate][chatMessages]', '*'],
				['[populate][chat][populate][cta]', '*'],
				['[populate][postCard][populate][cta]', '*'],
				[
					'[populate][postCard][populate][avatar][populate][image][populate]',
					'*',
				],
			],
		},
		topLevelPopulate: [['populate[seo][populate][metaImage][populate]', '*']],
	},
};

/**
 * Builds the full URLSearchParams entries for fetching a content type entry
 * by slug, derived from its registered block populate params and top-level
 * populate params.
 */
export function buildPopulateParams(
	contentType: string,
	slug: string,
): [string, string][] {
	const config = CONTENT_TYPE_POPULATE_CONFIGS[contentType];
	if (!config) return [['filters[slug][$eq]', slug]];

	const params: [string, string][] = [['filters[slug][$eq]', slug]];

	for (const [componentName, populateParams] of Object.entries(config.blocks)) {
		const prefix = `populate[blocks][on][${componentName}]`;
		for (const [keySuffix, value] of populateParams) {
			params.push([`${prefix}${keySuffix}`, value]);
		}
	}

	params.push(...config.topLevelPopulate);

	return params;
}
